<template>
  <v-card>
    <v-card-title class="d-flex align-center">
      <v-icon left>mdi-clock-outline</v-icon>
      <h5 class="font-weight-bold text-h5 text-typo mb-0">打卡紀錄</h5>
      <v-spacer />
      <v-progress-circular
        v-if="loading"
        indeterminate
        color="primary"
        size="24"
      />
    </v-card-title>

    <v-card-text>
      <v-row>
        <v-col cols="12" md="4" class="d-flex align-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
                @click="changeDate(-1)"
                class="mr-1"
              >
                <v-icon size="24">mdi-chevron-left</v-icon>
              </v-btn>
            </template>
            <span>昨天</span>
          </v-tooltip>

          <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="selectedDate"
                label="選擇日期"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                @click:clear="clearDate"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="selectedDate"
              @input="dateMenu = false"
              locale="zh-TW"
              color="blue"
            ></v-date-picker>
          </v-menu>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
                @click="changeDate(1)"
                class="ml-1"
              >
                <v-icon size="24">mdi-chevron-right</v-icon>
              </v-btn>
            </template>
            <span>明天</span>
          </v-tooltip>
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="searchQuery"
            label="搜尋員工姓名"
            prepend-icon="mdi-magnify"
            clearable
            @input="handleSearchInput"
          ></v-text-field>
        </v-col>
        <!-- 匯入按鈕 -->
        <v-col cols="12" md="4" class="d-flex justify-end">
          <v-btn
            :loading="uploading"
            :disabled="uploading"
            elevation="0"
            :ripple="false"
            height="43"
            class="
              font-weight-bold
              text-capitalize
              btn-primary
              bg-gradient-primary
              py-3
              px-6
            "
            @click="triggerClockRecordsUpload"
          >
            <v-icon size="18">mdi-plus</v-icon>
            匯入</v-btn
          >
        </v-col>
      </v-row>
    </v-card-text>

    <v-dialog v-model="clockRecordsUploadDialog" max-width="600px">
      <v-card class="card-shadow border-radius-xl">
        <div class="card-header-padding card-border-bottom">
          <span class="font-weight-bold text-h5 text-typo mb-0"
            >匯入打卡紀錄</span
          >
        </div>
        <v-card-text class="card-padding">
          <v-container class="px-0">
            <v-row>
              <v-col cols="12">
                <v-file-input
                  v-model="clockRecordsUploadFile"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  label="選擇 Excel 檔案 (.xlsx)"
                  prepend-icon="mdi-file-excel"
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" class="d-flex align-center">
                <v-icon size="18" class="me-2"
                  >mdi-file-download-outline</v-icon
                >
                <a
                  :href="sampleTemplateUrl"
                  target="_blank"
                  rel="noopener"
                  download
                >
                  下載範例檔
                </a>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions class="card-padding d-flex justify-end">
          <v-btn
            @click="cancelClockRecordsUpload"
            elevation="0"
            :ripple="false"
            height="43"
            class="
              font-weight-bold
              text-capitalize
              btn-ls btn-secondary
              bg-gradient-light
              py-3
              px-6
              me-2
            "
            :disabled="uploading"
            >取消</v-btn
          >
          <v-btn
            @click="confirmClockRecordsUpload"
            elevation="0"
            :ripple="false"
            height="43"
            class="
              font-weight-bold
              text-capitalize
              btn-ls btn-primary
              bg-gradient-primary
              py-3
              px-6
            "
            :loading="uploading"
            :disabled="uploading || !clockRecordsUploadFile"
            >確認</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-data-table
      :headers="headers"
      :items="filteredRecords"
      :loading="loading"
      :server-items-length="totalItems"
      :items-per-page="itemsPerPage"
      class="table"
      hide-default-footer
      disable-pagination
      @page-count="pageCount = $event"
      @update:items-per-page="handleItemsPerPageChange"
    >
      <template v-slot:progress>
        <v-progress-linear indeterminate color="primary" />
      </template>

      <template v-slot:item.clock_in_actual="{ item }">
        <div v-if="editedItem && editedItem.id === item.id">
          <date-time-picker
            :value="formatDateTime(editedItem.clock_in_actual)"
            :date.sync="clockInActualDate"
            :time.sync="clockInActualTime"
            @confirm="updateClockInActual"
          />
        </div>
        <div v-else class="d-flex align-center">
          <v-menu
            v-if="isValidGPS(item.gps_clock_in)"
            :value="activeGPSKey === item.id + '_in'"
            @input="(val) => (activeGPSKey = val ? item.id + '_in' : null)"
            offset-y
            :close-on-content-click="false"
            min-width="250"
          >
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                class="primary--text font-weight-bold text-sm"
                style="cursor: pointer; border-bottom: 1px dashed"
              >
                {{ formatDateTime(item.clock_in_actual) }}
              </span>
            </template>
            <v-card class="card-shadow border-radius-xl">
              <v-card-title
                class="pa-2 d-flex justify-space-between align-center"
              >
                <span class="text-subtitle-2 ml-2">GPS 位置 (上班)</span>
                <v-btn icon x-small @click="activeGPSKey = null">
                  <v-icon size="16">mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-divider />
              <v-card-text class="pa-3">
                <div class="mb-3 text-body-2 font-weight-bold">
                  {{ item.gps_clock_in }}
                </div>
                <v-btn
                  block
                  small
                  color="primary"
                  class="bg-gradient-primary"
                  @click="openGPSInGoogleMaps(item.gps_clock_in)"
                >
                  <v-icon left size="16">mdi-map-marker</v-icon>
                  開啟地圖
                </v-btn>
              </v-card-text>
            </v-card>
          </v-menu>
          <span v-else class="text-sm font-weight-normal text-body">
            {{ formatDateTime(item.clock_in_actual) }}
          </span>
        </div>
      </template>
      <template v-slot:item.clock_out_actual="{ item }">
        <div v-if="editedItem && editedItem.id === item.id">
          <date-time-picker
            :value="formatDateTime(editedItem.clock_out_actual)"
            :date.sync="clockOutActualDate"
            :time.sync="clockOutActualTime"
            @confirm="updateClockOutActual"
          />
        </div>
        <div v-else class="d-flex align-center">
          <v-menu
            v-if="isValidGPS(item.gps_clock_out)"
            :value="activeGPSKey === item.id + '_out'"
            @input="(val) => (activeGPSKey = val ? item.id + '_out' : null)"
            offset-y
            :close-on-content-click="false"
            min-width="250"
          >
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                class="primary--text font-weight-bold text-sm"
                style="cursor: pointer; border-bottom: 1px dashed"
              >
                {{ formatDateTime(item.clock_out_actual) }}
              </span>
            </template>
            <v-card class="card-shadow border-radius-xl">
              <v-card-title
                class="pa-2 d-flex justify-space-between align-center"
              >
                <span class="text-subtitle-2 ml-2">GPS 位置 (下班)</span>
                <v-btn icon x-small @click="activeGPSKey = null">
                  <v-icon size="16">mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-divider />
              <v-card-text class="pa-3">
                <div class="mb-3 text-body-2 font-weight-bold">
                  {{ item.gps_clock_out }}
                </div>
                <v-btn
                  block
                  small
                  color="primary"
                  class="bg-gradient-primary"
                  @click="openGPSInGoogleMaps(item.gps_clock_out)"
                >
                  <v-icon left size="16">mdi-map-marker</v-icon>
                  開啟地圖
                </v-btn>
              </v-card-text>
            </v-card>
          </v-menu>
          <span v-else class="text-sm font-weight-normal text-body">
            {{ formatDateTime(item.clock_out_actual) }}
          </span>
        </div>
      </template>
      <template v-slot:item.lunch_break="{ item }">
        <div v-if="editedItem && editedItem.id === item.id">
          <v-checkbox
            v-model="editedItem.lunch_break"
            dense
            hide-details
          ></v-checkbox>
        </div>
        <div v-else class="d-flex align-center">
          <span class="text-sm font-weight-normal text-body">
            {{ item.lunch_break ? "true" : "false" }}
          </span>
        </div>
      </template>
      <template v-slot:item.lunch_break_start_actual="{ item }">
        <div v-if="editedItem && editedItem.id === item.id">
          <date-time-picker
            :value="formatDateTime(editedItem.lunch_break_start_actual)"
            :date.sync="lunchBreakStartActualDate"
            :time.sync="lunchBreakStartActualTime"
            @confirm="updateLunchBreakStartActual"
          />
        </div>
        <div v-else class="d-flex align-center">
          <v-menu
            v-if="isValidGPS(item.gps_lunch_break_start)"
            :value="activeGPSKey === item.id + '_lb_start'"
            @input="
              (val) => (activeGPSKey = val ? item.id + '_lb_start' : null)
            "
            offset-y
            :close-on-content-click="false"
            min-width="250"
          >
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                class="primary--text font-weight-bold text-sm"
                style="cursor: pointer; border-bottom: 1px dashed"
              >
                {{ formatDateTime(item.lunch_break_start_actual) }}
              </span>
            </template>
            <v-card class="card-shadow border-radius-xl">
              <v-card-title
                class="pa-2 d-flex justify-space-between align-center"
              >
                <span class="text-subtitle-2 ml-2">GPS 位置 (午休開始)</span>
                <v-btn icon x-small @click="activeGPSKey = null">
                  <v-icon size="16">mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-divider />
              <v-card-text class="pa-3">
                <div class="mb-3 text-body-2 font-weight-bold">
                  {{ item.gps_lunch_break_start }}
                </div>
                <v-btn
                  block
                  small
                  color="primary"
                  class="bg-gradient-primary"
                  @click="openGPSInGoogleMaps(item.gps_lunch_break_start)"
                >
                  <v-icon left size="16">mdi-map-marker</v-icon>
                  開啟地圖
                </v-btn>
              </v-card-text>
            </v-card>
          </v-menu>
          <span v-else class="text-sm font-weight-normal text-body">
            {{ formatDateTime(item.lunch_break_start_actual) }}
          </span>
        </div>
      </template>
      <template v-slot:item.lunch_break_end_actual="{ item }">
        <div v-if="editedItem && editedItem.id === item.id">
          <date-time-picker
            :value="formatDateTime(editedItem.lunch_break_end_actual)"
            :date.sync="lunchBreakEndActualDate"
            :time.sync="lunchBreakEndActualTime"
            @confirm="updateLunchBreakEndActual"
          />
        </div>
        <div v-else class="d-flex align-center">
          <v-menu
            v-if="isValidGPS(item.gps_lunch_break_end)"
            :value="activeGPSKey === item.id + '_lb_end'"
            @input="(val) => (activeGPSKey = val ? item.id + '_lb_end' : null)"
            offset-y
            :close-on-content-click="false"
            min-width="250"
          >
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                class="primary--text font-weight-bold text-sm"
                style="cursor: pointer; border-bottom: 1px dashed"
              >
                {{ formatDateTime(item.lunch_break_end_actual) }}
              </span>
            </template>
            <v-card class="card-shadow border-radius-xl">
              <v-card-title
                class="pa-2 d-flex justify-space-between align-center"
              >
                <span class="text-subtitle-2 ml-2">GPS 位置 (午休結束)</span>
                <v-btn icon x-small @click="activeGPSKey = null">
                  <v-icon size="16">mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-divider />
              <v-card-text class="pa-3">
                <div class="mb-3 text-body-2 font-weight-bold">
                  {{ item.gps_lunch_break_end }}
                </div>
                <v-btn
                  block
                  small
                  color="primary"
                  class="bg-gradient-primary"
                  @click="openGPSInGoogleMaps(item.gps_lunch_break_end)"
                >
                  <v-icon left size="16">mdi-map-marker</v-icon>
                  開啟地圖
                </v-btn>
              </v-card-text>
            </v-card>
          </v-menu>
          <span v-else class="text-sm font-weight-normal text-body">
            {{ formatDateTime(item.lunch_break_end_actual) }}
          </span>
        </div>
      </template>
      <template v-slot:item.overtime="{ item }">
        <div v-if="editedItem && editedItem.id === item.id">
          <v-select
            v-model="editedItem.overtime"
            dense
            hide-details
            :items="[
              { text: '-', value: null },
              { text: '是', value: true },
              { text: '否', value: false },
            ]"
          ></v-select>
        </div>
        <div v-else class="d-flex align-center">
          <span class="text-sm font-weight-normal text-body">
            {{
              item.overtime !== null && item.overtime !== undefined
                ? item.overtime
                  ? "是"
                  : "否"
                : "-"
            }}
          </span>
        </div>
      </template>
      <template v-slot:item.overtime_record_id="{ item }">
        <div v-if="editedItem && editedItem.id === item.id">
          <v-text-field
            v-model.number="editedItem.overtime_record_id"
            dense
            hide-details
            type="number"
          ></v-text-field>
        </div>
        <div v-else class="d-flex align-center">
          <v-tooltip bottom v-if="getOvertimeRecords(item).length > 0">
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                class="text-sm font-weight-normal text-body"
                style="cursor: pointer; border-bottom: 1px dashed #ccc"
              >
                {{ getTotalOvertimeMinutes(item) }}分鐘
              </span>
            </template>
            <div class="d-flex flex-column">
              <span
                v-for="ot in getOvertimeRecords(item)"
                :key="`ot-${item.id}-${ot.id}`"
                class="mb-1"
              >
                {{ formatDateTime(ot.start_time) }} ~
                {{ formatDateTime(ot.end_time) }}
                ({{ Number(ot.overtime_minutes) || 0 }}分鐘)
              </span>
            </div>
          </v-tooltip>
          <span v-else class="text-sm font-weight-normal text-body">
            {{
              item.overtime_record_id !== null &&
              item.overtime_record_id !== undefined
                ? item.overtime_record_id
                : "-"
            }}
          </span>
        </div>
      </template>
      <template v-slot:item.leave_record_ids="{ item }">
        <div
          class="d-flex flex-column"
          v-if="item.leave_records && item.leave_records.length > 0"
        >
          <div
            v-for="leave in item.leave_records"
            :key="leave.id"
            class="text-sm"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                  style="cursor: pointer; border-bottom: 1px dashed #ccc"
                >
                  <span class="text-body font-weight-bold">
                    {{ getLeaveTypeName(leave.leave_type) }}
                  </span>
                  <span class="text-secondary ml-1">
                    {{ parseFloat((leave.leave_minutes / 60).toFixed(1)) }}小時
                  </span>
                </span>
              </template>
              <span>
                {{ formatDateTime(leave.start_time) }} ~
                {{ formatDateTime(leave.end_time) }}
              </span>
            </v-tooltip>
          </div>
        </div>
        <div v-else class="d-flex align-center">
          <span class="text-sm font-weight-normal text-body">
            {{
              item.leave_record_ids !== null &&
              item.leave_record_ids !== undefined
                ? item.leave_record_ids
                : "-"
            }}
          </span>
        </div>
      </template>
      <!-- extra -->
      <template v-slot:item.extra="{ item }">
        <div
          v-if="typeof item.extra === 'object' && item.extra"
          class="d-flex flex-column text-sm font-weight-normal text-body"
        >
          <div
            v-for="(val, key) in item.extra"
            :key="key"
            style="word-break: break-all"
            class="mb-1"
          >
            <template v-if="key === 're_clock_in'"
              >請假期間上班: {{ formatDateTime(val) }}</template
            >
            <template v-else-if="key === 're_clock_out'"
              >請假期間下班: {{ formatDateTime(val) }}</template
            >
          </div>
        </div>
        <div v-else class="d-flex align-center">
          <span
            class="text-sm font-weight-normal text-body"
            style="word-break: break-all"
          >
            {{ item.extra }}
          </span>
        </div>
      </template>

      <template v-slot:item.late="{ item }">
        <div v-if="editedItem && editedItem.id === item.id">
          <v-text-field
            v-model.number="editedItem.late"
            dense
            hide-details
            type="number"
          ></v-text-field>
        </div>
        <div v-else class="d-flex align-center">
          <span class="text-sm font-weight-normal text-body">
            {{ item.late !== null && item.late !== undefined ? item.late : 0 }}
          </span>
        </div>
      </template>

      <template v-slot:item.early="{ item }">
        <div v-if="editedItem && editedItem.id === item.id">
          <v-text-field
            v-model.number="editedItem.early"
            dense
            hide-details
            type="number"
          ></v-text-field>
        </div>
        <div v-else class="d-flex align-center">
          <span class="text-sm font-weight-normal text-body">
            {{
              item.early !== null && item.early !== undefined ? item.early : 0
            }}
          </span>
        </div>
      </template>

      <template v-slot:item.user_name="{ item }">
        <div class="d-flex align-center">
          <span
            class="text-sm font-weight-bold text-body hover-scale"
            style="cursor: pointer; text-decoration: underline"
            @click="
              item.user && item.user.id
                ? $emit('view-monthly', item.user.id)
                : null
            "
            title="查看每月打卡紀錄"
          >
            {{ item.user && item.user.name ? item.user.name : "" }}
          </span>
          <span
            v-if="item.businessTripOutingRecord"
            class="red--text font-weight-bold ml-1 text-sm"
          >
            ({{
              item.businessTripOutingRecord.category === "business_trip"
                ? "出差"
                : "外出"
            }})
          </span>
        </div>
      </template>
      <!-- actions -->
      <template v-slot:item.actions="{ item }">
        <div v-if="rootRole" class="d-flex align-center">
          <div v-if="editedItem && editedItem.id === item.id">
            <v-btn icon small color="red" @click="saveItem" class="mr-2">
              <v-icon size="20">mdi-content-save</v-icon>
            </v-btn>
            <v-btn icon small @click="cancelEdit">
              <v-icon size="20">mdi-close</v-icon>
            </v-btn>
          </div>
          <div v-else>
            <v-btn
              @click.stop="editItem(item)"
              icon
              elevation="0"
              :ripple="false"
              height="28"
              min-width="36"
              width="36"
              class="btn-ls me-2 my-2 rounded-sm"
              color="#67748e"
              title="編輯"
            >
              <v-icon size="18">mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              @click.stop="viewHistory(item)"
              icon
              elevation="0"
              :ripple="false"
              height="28"
              min-width="36"
              width="36"
              class="btn-ls me-2 my-2 rounded-sm"
              color="#cb0c9f"
              title="歷史記錄"
            >
              <v-icon size="18">mdi-history</v-icon>
            </v-btn>
          </div>
        </div>
      </template>

      <template v-slot:body.append>
        <tr v-if="error">
          <td
            :colspan="headers.length"
            class="text-center red--text text-body-2"
          >
            {{ error }}
          </td>
        </tr>
        <tr v-else-if="!loading && !records.length">
          <td
            :colspan="headers.length"
            class="text-center grey--text text-body-2"
          >
            無資料
          </td>
        </tr>
      </template>
    </v-data-table>

    <div id="sentinel-clock" class="d-flex justify-center py-4">
      <v-progress-circular
        v-if="loadingMore"
        indeterminate
        color="#cb0c9f"
        size="24"
      ></v-progress-circular>
    </div>

    <info-dialog
      :visible.sync="infoDialog.visible"
      :title="infoDialog.title"
      :message="infoDialog.message"
      :yes-button-text="infoDialog.yesButtonText"
      :yes-callback="infoDialog.yesCallback"
    />

    <v-dialog v-model="historyDialog" max-width="900px" scrollable>
      <v-card class="card-shadow border-radius-xl">
        <div class="card-header-padding card-border-bottom">
          <span class="font-weight-bold text-h5 text-typo mb-0"
            >修改歷史記錄</span
          >
        </div>
        <v-card-text class="card-padding" style="max-height: 600px">
          <v-progress-linear
            v-if="historyLoading"
            indeterminate
            color="primary"
          />
          <div v-else-if="historyError" class="red--text text-center py-4">
            {{ historyError }}
          </div>
          <div
            v-else-if="!historyRecords.length"
            class="grey--text text-center py-4"
          >
            無修改記錄
          </div>
          <v-timeline v-else dense>
            <v-timeline-item
              v-for="record in historyRecords"
              :key="record.id"
              small
              color="primary"
            >
              <template v-slot:opposite>
                <span class="text-caption">
                  {{ formatDateTime(record.create_time) }}
                </span>
              </template>
              <v-card class="elevation-2">
                <v-card-text>
                  <div class="font-weight-bold mb-2">
                    修改人: {{ record.modifier ? record.modifier.name : "-" }}
                    <span
                      v-if="record.modifier && record.modifier.email"
                      class="text-caption grey--text"
                    >
                      ({{ record.modifier.email }})
                    </span>
                  </div>
                  <div class="text-caption grey--text mb-2">
                    {{ formatDateTime(record.create_time) }}
                  </div>
                  <v-divider class="my-2" />
                  <div class="text-body-2">
                    <div
                      v-for="(change, field) in record.changes"
                      :key="field"
                      class="mb-2"
                    >
                      <strong>{{ getFieldLabel(field) }}:</strong>
                      <div class="ml-4">
                        <div class="red--text text--lighten-1">
                          舊值: {{ formatFieldValue(field, change.old) }}
                        </div>
                        <div class="green--text text--darken-1">
                          新值: {{ formatFieldValue(field, change.new) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-timeline-item>
          </v-timeline>
          <div v-if="historyMeta.totalPages > 1" class="text-center mt-4">
            <v-pagination
              v-model="historyPage"
              :length="historyMeta.totalPages"
              @input="loadHistory"
              circle
              color="primary"
            />
          </div>
        </v-card-text>
        <v-card-actions class="card-padding d-flex justify-end">
          <v-btn
            @click="closeHistory"
            elevation="0"
            :ripple="false"
            height="43"
            class="
              font-weight-bold
              text-capitalize
              btn-ls btn-secondary
              bg-gradient-light
              py-3
              px-6
            "
            >關閉</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import DateTimePicker from "@/components/Common/DateTimePicker.vue";
import InfoDialog from "@/components/Common/InfoDialog.vue";
import { formatDateTime as formatDateTimeUtil } from "@/utils/time";
import { LEAVE_TYPE } from "@/constants";

const getTodayInTaipei = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Taipei",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

export default {
  name: "DailyClockRecords",
  components: {
    DateTimePicker,
    InfoDialog,
  },
  props: {
    date: {
      type: String,
      default: () => {
        return getTodayInTaipei();
      },
    },
  },
  data() {
    return {
      records: [],
      loading: false,
      error: null,
      fetchError: false,
      selectedDate: getTodayInTaipei(),
      dateMenu: false,
      searchQuery: "",
      debouncedSearch: "",
      searchDebounceTimer: null,
      uploading: false,
      clockRecordsUploadDialog: false,
      clockRecordsUploadFile: null,
      infoDialog: {
        visible: false,
        title: "匯入結果",
        message: "",
        yesButtonText: "確定",
        yesCallback: null,
      },
      pageCount: 0,
      currentPage: 1,
      itemsPerPage: 15,
      totalItems: 0,
      editedItem: null,
      originalItem: null,
      clockInActualDate: null,
      clockInActualTime: null,
      clockOutActualDate: null,
      clockOutActualTime: null,
      clockInTimeDate: null,
      clockInTimeTime: null,
      clockOutTimeDate: null,
      clockOutTimeTime: null,
      lunchBreakStartDate: null,
      lunchBreakStartTime: null,
      lunchBreakStartActualDate: null,
      lunchBreakStartActualTime: null,
      lunchBreakEndDate: null,
      lunchBreakEndTime: null,
      lunchBreakEndActualDate: null,
      lunchBreakEndActualTime: null,
      historyDialog: false,
      historyLoading: false,
      historyError: null,
      historyRecords: [],
      historyMeta: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      },
      historyPage: 1,
      currentHistoryRecordId: null,
      headers: [
        {
          text: "員工",
          value: "user_name",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "實際上班",
          value: "clock_in_actual",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "實際下班",
          value: "clock_out_actual",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "午休實際開始",
          value: "lunch_break_start_actual",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "午休實際結束",
          value: "lunch_break_end_actual",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "加班",
          value: "overtime",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "加班申請",
          value: "overtime_record_id",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "請假紀錄",
          value: "leave_record_ids",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        // extra
        {
          text: "備註",
          value: "extra",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "遲到",
          value: "late",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "早退",
          value: "early",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "",
          value: "actions",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
      ],
      activeGPSKey: null,
      loadingMore: false,
      observer: null,
    };
  },
  computed: {
    ...mapGetters("company", ["selectedCompany"]),
    ...mapGetters("user", ["rootRole"]),
    sampleTemplateUrl() {
      return `${process.env.VUE_APP_API_URL}/resource/samples/clock_history.xlsx`;
    },
    filteredRecords() {
      const query = (this.debouncedSearch || "").trim().toLowerCase();
      if (!query) {
        return this.records;
      }
      return this.records.filter((record) => {
        const userName =
          record.user && record.user.name ? record.user.name.toLowerCase() : "";
        return userName.includes(query);
      });
    },
  },
  created() {
    this.debouncedSearch = this.searchQuery;
    this.fetchAttendanceRecords();
  },
  mounted() {
    this.initIntersectionObserver();
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  watch: {
    selectedDate() {
      this.currentPage = 1;
      this.fetchAttendanceRecords();
    },
    selectedCompany: {
      handler() {
        this.currentPage = 1;
        this.fetchAttendanceRecords();
      },
      deep: true,
    },
  },
  methods: {
    async fetchAttendanceRecords(append = false) {
      if (append) {
        this.loadingMore = true;
      } else {
        this.loading = true;
      }
      this.error = null;
      if (!this.selectedCompany) {
        this.records = [];
        this.loading = false;
        this.loadingMore = false;
        return;
      }
      this.fetchError = false;
      const queryParams = new URLSearchParams({
        page: this.currentPage,
        limit: this.itemsPerPage,
        start_date: this.selectedDate,
        end_date: this.selectedDate,
        company_id: this.selectedCompany.id,
      });

      try {
        const response = await this.$axios.get(
          `/api/clock-records?${queryParams}`
        );
        if (append) {
          this.records = [...this.records, ...(response.data.rows || [])];
        } else {
          this.records = response.data.rows || [];
        }

        this.totalItems = response.data.pagination
          ? response.data.pagination.totalItems
          : 0;
      } catch (error) {
        this.records = [];
        this.error =
          error?.response?.data?.message ||
          error.message ||
          "取得資料時發生錯誤";
        this.fetchError = true;
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },
    initIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      };
      this.observer = new IntersectionObserver((entries) => {
        const target = entries[0];
        if (
          target.isIntersecting &&
          !this.loading &&
          !this.loadingMore &&
          !this.fetchError &&
          this.currentPage < this.pageCount
        ) {
          this.currentPage += 1;
          this.fetchAttendanceRecords(true);
        }
      }, options);

      const target = document.querySelector("#sentinel-clock");
      if (target) {
        this.observer.observe(target);
      }
    },
    formatDateTime(value) {
      return formatDateTimeUtil(value);
    },
    getOvertimeRecords(item) {
      if (!item) return [];
      if (Array.isArray(item.overtimeRecords)) {
        return item.overtimeRecords;
      }
      if (item.overtimeRecord && typeof item.overtimeRecord === "object") {
        return [item.overtimeRecord];
      }
      return [];
    },
    getTotalOvertimeMinutes(item) {
      return this.getOvertimeRecords(item).reduce((sum, ot) => {
        const minutes = Number(ot && ot.overtime_minutes);
        return sum + (Number.isFinite(minutes) ? minutes : 0);
      }, 0);
    },
    getLeaveTypeName(typeId) {
      return LEAVE_TYPE[typeId] || `假別${typeId}`;
    },

    handleItemsPerPageChange(itemsPerPage) {
      this.itemsPerPage = itemsPerPage;
      this.currentPage = 1;
      this.fetchAttendanceRecords();
    },
    handleSearchInput() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.debouncedSearch = this.searchQuery;
        this.currentPage = 1;
      }, 300);
    },
    triggerClockRecordsUpload() {
      if (this.uploading) return;
      this.clockRecordsUploadFile = null;
      this.clockRecordsUploadDialog = true;
    },

    cancelClockRecordsUpload() {
      if (this.uploading) return;
      this.clockRecordsUploadDialog = false;
      this.clockRecordsUploadFile = null;
    },
    async confirmClockRecordsUpload() {
      if (!this.clockRecordsUploadFile) {
        alert("請先選擇要匯入的檔案");
        return;
      }
      this.clockRecordsUploadDialog = false;
      await this.uploadClockRecordsFile(this.clockRecordsUploadFile);
    },
    async uploadClockRecordsFile(file) {
      if (!file) return;
      if (!this.selectedCompany || !this.selectedCompany.id) {
        alert("請先選擇公司再匯入");
        return;
      }
      const formData = new FormData();
      formData.append("file", file);
      formData.append("company_id", this.selectedCompany.id);
      this.uploading = true;
      try {
        const response = await this.$axios.post(
          "/api/clock-records/bulk-upload",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        const { message, summary } = response?.data || {};
        const successCount = summary?.success;
        const errorCount = summary?.errors;
        const errorDetails = (response?.data?.results?.errors || [])
          .map((err) => `第 ${err.row} 列 - ${err.error}`)
          .join("\n");
        const summaryMessage =
          message ||
          `匯入完成${
            typeof successCount === "number" ? `，成功 ${successCount} 筆` : ""
          }${typeof errorCount === "number" ? `，失敗 ${errorCount} 筆` : ""}`;
        this.infoDialog.message = summaryMessage;
        if (errorDetails && errorCount > 0) {
          this.infoDialog.message += `\n\n失敗明細:\n${errorDetails}`;
        }
        this.infoDialog.title = "匯入結果";
        this.infoDialog.yesCallback = async () => {
          await this.fetchAttendanceRecords();
          this.infoDialog.yesCallback = null;
        };
        this.infoDialog.visible = true;
      } catch (error) {
        const errMsg =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error.message ||
          "匯入失敗";
        this.infoDialog.message = errMsg;
        this.infoDialog.title = "匯入失敗";
        this.infoDialog.yesCallback = null;
        this.infoDialog.visible = true;
      } finally {
        this.uploading = false;
        this.clockRecordsUploadFile = null;
      }
    },
    clearDate() {
      this.selectedDate = getTodayInTaipei();
    },
    changeDate(offset) {
      const current = new Date(this.selectedDate);
      current.setDate(current.getDate() + offset);
      this.selectedDate = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Taipei",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(current);
    },
    editItem(item) {
      this.editedItem = Object.assign({}, item);
      this.originalItem = Object.assign({}, item); // Keep a copy of the original item
      // Initialize date and time pickers
      this.initializeDateTimePickers();
    },
    initializeDateTimePickers() {
      if (!this.editedItem) return;

      // Parse existing datetime values
      this.parseDateTime(
        "clock_in_actual",
        "clockInActualDate",
        "clockInActualTime",
        this.selectedDate
      );
      this.parseDateTime(
        "clock_out_actual",
        "clockOutActualDate",
        "clockOutActualTime",
        this.selectedDate
      );
      this.parseDateTime("clock_in_time", "clockInTimeDate", "clockInTimeTime");
      this.parseDateTime(
        "clock_out_time",
        "clockOutTimeDate",
        "clockOutTimeTime"
      );
      this.parseDateTime(
        "lunch_break_start",
        "lunchBreakStartDate",
        "lunchBreakStartTime"
      );
      this.parseDateTime(
        "lunch_break_start_actual",
        "lunchBreakStartActualDate",
        "lunchBreakStartActualTime",
        this.selectedDate
      );
      this.parseDateTime(
        "lunch_break_end",
        "lunchBreakEndDate",
        "lunchBreakEndTime"
      );
      this.parseDateTime(
        "lunch_break_end_actual",
        "lunchBreakEndActualDate",
        "lunchBreakEndActualTime",
        this.selectedDate
      );
    },
    parseDateTime(field, dateField, timeField, defaultDate = null) {
      const value = this.editedItem[field];
      if (value) {
        const dt = new Date(value);
        if (!Number.isNaN(dt.getTime())) {
          this[dateField] = dt.toISOString().split("T")[0];
          this[timeField] = dt.toTimeString().split(" ")[0].substring(0, 5);
        }
      } else {
        // Set default to current date and time when value is null
        const now = new Date();
        this[dateField] = defaultDate || now.toISOString().split("T")[0];
        this[timeField] = now.toTimeString().split(" ")[0].substring(0, 5);
      }
    },
    combineDateTimeToISO(date, time) {
      if (!date) return null;
      const timeStr = time || "00:00";
      return new Date(`${date}T${timeStr}:00+08:00`).toISOString();
    },
    cancelEdit() {
      this.editedItem = null;
      this.resetDateTimePickers();
    },
    resetDateTimePickers() {
      this.clockInActualDate = null;
      this.clockInActualTime = null;
      this.clockOutActualDate = null;
      this.clockOutActualTime = null;
      this.clockInTimeDate = null;
      this.clockInTimeTime = null;
      this.clockOutTimeDate = null;
      this.clockOutTimeTime = null;
      this.lunchBreakStartDate = null;
      this.lunchBreakStartTime = null;
      this.lunchBreakStartActualDate = null;
      this.lunchBreakStartActualTime = null;
      this.lunchBreakEndDate = null;
      this.lunchBreakEndTime = null;
      this.lunchBreakEndActualDate = null;
      this.lunchBreakEndActualTime = null;
    },
    updateClockInActual() {
      console.log(
        "updateClockInActual",
        this.clockInActualDate,
        this.clockInActualTime
      );
      this.editedItem.clock_in_actual = this.combineDateTimeToISO(
        this.clockInActualDate,
        this.clockInActualTime
      );
    },
    updateClockOutActual() {
      console.log(
        "updateClockOutActual",
        this.clockOutActualDate,
        this.clockOutActualTime
      );
      this.editedItem.clock_out_actual = this.combineDateTimeToISO(
        this.clockOutActualDate,
        this.clockOutActualTime
      );
    },
    updateClockInTime() {
      this.editedItem.clock_in_time = this.combineDateTimeToISO(
        this.clockInTimeDate,
        this.clockInTimeTime
      );
    },
    updateClockOutTime() {
      this.editedItem.clock_out_time = this.combineDateTimeToISO(
        this.clockOutTimeDate,
        this.clockOutTimeTime
      );
    },
    updateLunchBreakStart() {
      this.editedItem.lunch_break_start = this.combineDateTimeToISO(
        this.lunchBreakStartDate,
        this.lunchBreakStartTime
      );
    },
    updateLunchBreakStartActual() {
      this.editedItem.lunch_break_start_actual = this.combineDateTimeToISO(
        this.lunchBreakStartActualDate,
        this.lunchBreakStartActualTime
      );
    },
    updateLunchBreakEnd() {
      this.editedItem.lunch_break_end = this.combineDateTimeToISO(
        this.lunchBreakEndDate,
        this.lunchBreakEndTime
      );
    },
    updateLunchBreakEndActual() {
      this.editedItem.lunch_break_end_actual = this.combineDateTimeToISO(
        this.lunchBreakEndActualDate,
        this.lunchBreakEndActualTime
      );
    },
    async saveItem() {
      if (!this.editedItem) return;

      try {
        const payload = {
          clock_in_actual: this.editedItem.clock_in_actual,
          clock_out_actual: this.editedItem.clock_out_actual,
          lunch_break_start_actual: this.editedItem.lunch_break_start_actual,
          lunch_break_end_actual: this.editedItem.lunch_break_end_actual,
          rest_1_start_actual: this.editedItem.rest_1_start_actual || null,
          rest_1_end_actual: this.editedItem.rest_1_end_actual || null,
          overtime: this.editedItem.overtime,
          // overtime_apply_id: this.editedItem.overtime_apply_id || null,
        };

        if (this.editedItem.late !== this.originalItem.late) {
          payload.late = this.editedItem.late;
        }

        if (this.editedItem.early !== this.originalItem.early) {
          payload.early = this.editedItem.early;
        }

        const response = await this.$axios.put(
          `/api/clock-records/${this.editedItem.id}`,
          payload
        );

        // Update the record in the list using response data
        const updatedData = response.data.data;
        const index = this.records.findIndex(
          (r) => r.id === this.editedItem.id
        );
        if (index !== -1 && updatedData) {
          this.records.splice(index, 1, updatedData);
        }

        this.editedItem = null;
        this.$emit("success", "更新成功");
      } catch (error) {
        this.error =
          error?.response?.data?.message || error.message || "更新失敗";
        const errMsg =
          error?.response?.data?.message || error.message || "更新失敗";
        this.infoDialog = {
          visible: true,
          title: "更新失敗",
          message: errMsg,
        };
      }
    },
    async viewHistory(item) {
      this.currentHistoryRecordId = item.id;
      this.historyPage = 1;
      this.historyDialog = true;
      await this.loadHistory();
    },
    async loadHistory() {
      if (!this.currentHistoryRecordId) return;

      this.historyLoading = true;
      this.historyError = null;

      try {
        const queryParams = new URLSearchParams({
          page: this.historyPage,
          limit: this.historyMeta.limit,
        });

        const response = await this.$axios.get(
          `/api/history/clock-records/${this.currentHistoryRecordId}?${queryParams}`
        );

        this.historyRecords = response.data.data || [];
        this.historyMeta = response.data.meta || {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
        };
      } catch (error) {
        this.historyError =
          error?.response?.data?.message || error.message || "無法載入歷史記錄";
        this.historyRecords = [];
      } finally {
        this.historyLoading = false;
      }
    },
    closeHistory() {
      this.historyDialog = false;
      this.currentHistoryRecordId = null;
      this.historyRecords = [];
      this.historyError = null;
      this.historyPage = 1;
    },
    getFieldLabel(field) {
      const labels = {
        clock_in_actual: "實際上班",
        clock_out_actual: "實際下班",
        lunch_break: "午休",
        lunch_break_start_actual: "午休實際開始",
        lunch_break_end_actual: "午休實際結束",
        overtime: "加班",
        overtime_apply_id: "加班申請",
        overtime_record_id: "加班申請",
        late: "遲到",
        early: "早退",
        clock_in_time: "排定上班",
        clock_out_time: "排定下班",
        lunch_break_start: "午休排定開始",
        lunch_break_end: "午休排定結束",
      };
      return labels[field] || field;
    },
    formatFieldValue(field, value) {
      if (value === null || value === undefined) {
        return "-";
      }

      // Handle datetime fields
      const dateTimeFields = [
        "clock_in_actual",
        "clock_out_actual",
        "lunch_break_start_actual",
        "lunch_break_end_actual",
        "clock_in_time",
        "clock_out_time",
        "lunch_break_start",
        "lunch_break_end",
      ];

      if (dateTimeFields.includes(field)) {
        return this.formatDateTime(value);
      }

      // Handle boolean fields
      if (field === "lunch_break") {
        return value ? "是" : "否";
      }

      if (field === "overtime") {
        return value === true ? "是" : value === false ? "否" : "-";
      }

      // Handle numeric fields
      if (typeof value === "number") {
        return value.toString();
      }

      return value;
    },
    isValidGPS(gps) {
      if (!gps || typeof gps !== "string") return false;
      const parts = gps.split(",");
      if (parts.length !== 2) return false;
      const lat = parseFloat(parts[0].trim());
      const lon = parseFloat(parts[1].trim());
      return !isNaN(lat) && !isNaN(lon);
    },
    openGPSInGoogleMaps(gps) {
      if (!this.isValidGPS(gps)) return;
      const [latStr, lonStr] = gps.split(",");
      const lat = parseFloat(latStr.trim());
      const lon = parseFloat(lonStr.trim());
      const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`;
      window.open(url, "_blank");
    },
  },
};
</script>

<style scoped>
.table >>> thead th {
  white-space: nowrap;
  font-size: 1rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.025rem;
  color: #8392ab;
  opacity: 0.7;
  padding: 0.75rem 1.5rem;
  border-bottom: 0.0625rem solid #e9ecef;
}

.table >>> tbody tr:last-child td {
  border-bottom: 0;
}

.table >>> tbody td {
  padding: 0.75rem 1.5rem;
  border-bottom: 0.0625rem solid #e9ecef;
}

.table >>> tbody tr:hover {
  background: #f8f9fa;
}
</style>
